import { useContext } from "react";
import { Button, Col, Row, Tag } from "antd";
import {
    CustomerServiceOutlined,
    GlobalOutlined,
    MobileOutlined,
    ReloadOutlined,
    UserOutlined,
} from "@ant-design/icons";

import useHelpAndSupport from "./useHelpAndSupport";
import PageHeader from "../../components/PageHeader";
import TabAppUsers from "./components/Tabs/TabAppUsers";
import TabExternalSupport from "./components/Tabs/TabExternalSupport";
import { ThemeContext } from "../../contexts/ThemeContext";
import { poeValorComPontosEmMil } from "../../utils/Conversores";

export default function HelpAndSupport() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        activeTab,
        handleChangeTab,
        refreshKey,
        isRefreshing,
        handleRefreshAll,
        counts,
        totalAllTickets,
        canManageConfig,
    } = useHelpAndSupport();

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <PageHeader
                icon={<CustomerServiceOutlined />}
                iconBg="linear-gradient(135deg, #1890ff 0%, #0958d9 100%)"
                title="Ajuda e Suporte"
                tag={<Tag color="blue">{poeValorComPontosEmMil(totalAllTickets)} registros</Tag>}
                subtitle="Gerencie, visualize e responda aos chamados e mensagens de suporte recebidas dos cidadãos e do portal"
                actions={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshAll}
                        loading={isRefreshing}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Atualizar
                    </Button>
                }
            />

            {/* Clean Clickable Card Tabs */}
            <Row gutter={[12, 12]}>
                {/* Card Tab 1: Usuários (App) */}
                <Col xs={24} sm={canManageConfig ? 8 : 12}>
                    <div
                        onClick={() => handleChangeTab("usuarios")}
                        role="button"
                        tabIndex={0}
                        style={{
                            background:
                                activeTab === "usuarios"
                                    ? isDark
                                        ? "#112a45"
                                        : "#e6f4ff"
                                    : isDark
                                    ? "#141414"
                                    : "#ffffff",
                            border:
                                activeTab === "usuarios"
                                    ? "2px solid #1677ff"
                                    : `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                            borderRadius: 10,
                            padding: "12px 16px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            transition: "all 0.2s ease",
                            boxShadow:
                                activeTab === "usuarios"
                                    ? "0 2px 10px rgba(22, 119, 255, 0.2)"
                                    : "0 1px 3px rgba(0,0,0,0.03)",
                            userSelect: "none",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 8,
                                    background:
                                        activeTab === "usuarios"
                                            ? "#1677ff"
                                            : isDark
                                            ? "#222"
                                            : "#f1f5f9",
                                    color:
                                        activeTab === "usuarios"
                                            ? "#ffffff"
                                            : isDark
                                            ? "#8c8c8c"
                                            : "#64748b",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 18,
                                    flexShrink: 0,
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <UserOutlined />
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color:
                                            activeTab === "usuarios"
                                                ? isDark
                                                    ? "#69b1ff"
                                                    : "#0958d9"
                                                : isDark
                                                ? "#f0f0f0"
                                                : "#1e293b",
                                    }}
                                >
                                    Usuários (App)
                                </div>
                                <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                    {poeValorComPontosEmMil(counts.appUsers)} chamado{counts.appUsers === 1 ? "" : "s"}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {counts.appUsersUnread > 0 ? (
                                <Tag
                                    color="error"
                                    style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}
                                >
                                    {counts.appUsersUnread} não lido{counts.appUsersUnread > 1 ? "s" : ""}
                                </Tag>
                            ) : counts.appUsersOpen > 0 ? (
                                <Tag
                                    color="warning"
                                    style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}
                                >
                                    {counts.appUsersOpen} aberto{counts.appUsersOpen > 1 ? "s" : ""}
                                </Tag>
                            ) : null}
                        </div>
                    </div>
                </Col>

                {/* Card Tab 2: Suporte Externo (App) */}
                <Col xs={24} sm={canManageConfig ? 8 : 12}>
                    <div
                        onClick={() => handleChangeTab("externo-app")}
                        role="button"
                        tabIndex={0}
                        style={{
                            background:
                                activeTab === "externo-app"
                                    ? isDark
                                        ? "#2e1e0a"
                                        : "#fff7e6"
                                    : isDark
                                    ? "#141414"
                                    : "#ffffff",
                            border:
                                activeTab === "externo-app"
                                    ? "2px solid #fa8c16"
                                    : `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                            borderRadius: 10,
                            padding: "12px 16px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            transition: "all 0.2s ease",
                            boxShadow:
                                activeTab === "externo-app"
                                    ? "0 2px 10px rgba(250, 140, 22, 0.2)"
                                    : "0 1px 3px rgba(0,0,0,0.03)",
                            userSelect: "none",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 8,
                                    background:
                                        activeTab === "externo-app"
                                            ? "#fa8c16"
                                            : isDark
                                            ? "#222"
                                            : "#f1f5f9",
                                    color:
                                        activeTab === "externo-app"
                                            ? "#ffffff"
                                            : isDark
                                            ? "#8c8c8c"
                                            : "#64748b",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 18,
                                    flexShrink: 0,
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <MobileOutlined />
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color:
                                            activeTab === "externo-app"
                                                ? isDark
                                                    ? "#ffc069"
                                                    : "#d46b08"
                                                : isDark
                                                ? "#f0f0f0"
                                                : "#1e293b",
                                    }}
                                >
                                    Suporte Externo (App)
                                </div>
                                <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                    {poeValorComPontosEmMil(counts.externalApp)} mensagen{counts.externalApp === 1 ? "m" : "s"}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {counts.externalAppOpen > 0 ? (
                                <Tag
                                    color="warning"
                                    style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}
                                >
                                    {counts.externalAppOpen} aberta{counts.externalAppOpen > 1 ? "s" : ""}
                                </Tag>
                            ) : null}
                        </div>
                    </div>
                </Col>

                {/* Card Tab 3: Contato (Site) */}
                {canManageConfig && (
                    <Col xs={24} sm={8}>
                        <div
                            onClick={() => handleChangeTab("externo-site")}
                            role="button"
                            tabIndex={0}
                            style={{
                                background:
                                    activeTab === "externo-site"
                                        ? isDark
                                            ? "#1a1329"
                                            : "#f9f0ff"
                                        : isDark
                                        ? "#141414"
                                        : "#ffffff",
                                border:
                                    activeTab === "externo-site"
                                        ? "2px solid #722ed1"
                                        : `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                                borderRadius: 10,
                                padding: "12px 16px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                transition: "all 0.2s ease",
                                boxShadow:
                                    activeTab === "externo-site"
                                        ? "0 2px 10px rgba(114, 46, 209, 0.2)"
                                        : "0 1px 3px rgba(0,0,0,0.03)",
                                userSelect: "none",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div
                                    style={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: 8,
                                        background:
                                            activeTab === "externo-site"
                                                ? "#722ed1"
                                                : isDark
                                                ? "#222"
                                                : "#f1f5f9",
                                        color:
                                            activeTab === "externo-site"
                                                ? "#ffffff"
                                                : isDark
                                                ? "#8c8c8c"
                                                : "#64748b",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 18,
                                        flexShrink: 0,
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <GlobalOutlined />
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color:
                                                activeTab === "externo-site"
                                                    ? isDark
                                                        ? "#b37feb"
                                                        : "#531dab"
                                                    : isDark
                                                    ? "#f0f0f0"
                                                    : "#1e293b",
                                        }}
                                    >
                                        Contato (Site)
                                    </div>
                                    <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                        {poeValorComPontosEmMil(counts.externalSite)} mensagen{counts.externalSite === 1 ? "m" : "s"}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {counts.externalSiteOpen > 0 ? (
                                    <Tag
                                        color="purple"
                                        style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}
                                    >
                                        {counts.externalSiteOpen} aberta{counts.externalSiteOpen > 1 ? "s" : ""}
                                    </Tag>
                                ) : null}
                            </div>
                        </div>
                    </Col>
                )}
            </Row>

            {/* Active Tab Content */}
            <div>
                {activeTab === "usuarios" && (
                    <TabAppUsers key={`tab-app-users-${refreshKey}`} />
                )}
                {activeTab === "externo-app" && (
                    <TabExternalSupport key={`tab-ext-app-${refreshKey}`} origin="Aplicativo" />
                )}
                {activeTab === "externo-site" && canManageConfig && (
                    <TabExternalSupport key={`tab-ext-site-${refreshKey}`} origin="Site" />
                )}
            </div>
        </div>
    );
}
